import { Component, OnInit, Renderer2 } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { ApiService } from 'src/app/shared/api.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { SitesSelectorComponent } from 'src/app/shared/sites-selector/sites-selector.component';
import { HazardousSubstanceModel } from '../../models/hazardous-substance.model';
import { PpeSelectorComponent } from '../../shared/ppe-selector/ppe-selector.component';
import moment from 'moment';
import { ApiRequestService } from '../../shared/api-request.service';

@Component({
  selector: 'app-hazardous-substances-edit',
  templateUrl: './hazardous-substances-edit.component.html',
  styleUrls: ['./hazardous-substances-edit.component.css']
})
export class HazardousSubstancesEditComponent implements OnInit {

  hazardous_substance: HazardousSubstanceModel = new HazardousSubstanceModel();

  search = '';

  selectedFiles: File[] = [];

  inProgress = false;

  // The current date for min expiry date selection.
  currentDate: Date = moment().toDate();

  // The selected expiry date object.
  selectedExpiryDate: any = null;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public app: AppService,
    private oldApi: ApiService,
    public utils: UtilsService,
    public renderer: Renderer2,
    private api: ApiRequestService
  ) {}

  ngOnInit() {
    // get the site id
    this.hazardous_substance.id = Number(this.route.snapshot.params['hazardous_substance_id']);

    // Get the hazardous substance info.
    this.find();
  }

  /**
   * Handles the selection of files and adds them to the list of selected files.
   *
   * @return {void} This method does not return a value.
   * @param fileList
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Uploads the selected files to the specified endpoint associated with the provided hazardous substance ID.
   *
   * @return {void}
   * @param id
   * @param callback
   */
  private uploadSelectedFiles(id: number, callback?: any): void {
    if ( id && this.selectedFiles.length > 0 ) {
      this.api.makeUploadRequest(`v2/file-manager/hazardous_substance/${id}`, this.selectedFiles)
        .then((response: any): void => {
          this.utils.showToast('Your files successfully uploaded.');
        })
        .finally((): void => {
          this.selectedFiles.length = 0;
          if ( typeof callback === 'function' ) {
            callback();
          }
        });
    }
  }

  onSelectSites() {
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: this.hazardous_substance.site_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.hazardous_substance.site_ids = results;
        }

        // TBD: refresh a list of selected sites?
      }
    );
  }

  async onSubmit(form: NgForm) {
    // Check if the submitted form is valid.
    if ( !form.valid ) {
      alert('Please make sure you enter all fields with valid information.');
      return;
    }

    // Check if another API request is in progress.
    if ( this.inProgress ) {
      return;
    }

    this.inProgress = true;

    // Prepare the SDS expiry date.
    this.hazardous_substance.sds_expires_at = this.selectedExpiryDate ? this.selectedExpiryDate.unix() : null;

    // Check if we need to create a new or update an existing hazardous substance.
    if ( this.hazardous_substance.id ) {
      this.update();
    } else {
      this.create();
    }
  }

  getRiskAssessmentColorChange(likelihood: number, severity: number) {
    const riskAssessment = this.utils.getRiskAssessmentText(
      likelihood,
      severity
    );

    if (['Low'].find((value) => value === riskAssessment)) {
      return 'success';
    }

    if (['Moderate'].find((value) => value === riskAssessment)) {
      return 'warning';
    }

    if (['High', 'Critical'].find((value) => value === riskAssessment)) {
      return 'danger';
    }

    return 'danger';
  }

  onRemove() {
    const singular = this.utils.getLangTerm('hazardous-substances.singular', 'Hazardous Substance');
    this.utils.showModal(
      `Remove ${singular}`,
      `Are you sure you want to remove this ${singular}?`,
      () => {
        this.oldApi.laravelApiRequest(
          'delete',
          'hazardous_substances/' + this.hazardous_substance.id,
          {},
          {},
          () => {
            this.utils.showToast(`The ${singular} was removed`);
            this.router.navigate(['hazardous_substances']);
          },
          (error) => {
            this.utils.showModal('Error', error.message);
          }
        );
      }
    );
  }

  private find() {
    // Check if we are viewing an existing hazardous substance.
    if ( this.hazardous_substance.id ) {
      this.oldApi.laravelApiRequest('get', 'hazardous_substances/' + this.hazardous_substance.id, {}, {}, (response) => {
        // Store the hazardous substance.
        this.hazardous_substance.apply(response.data);

        // Extract the PPE ids.
        this.hazardous_substance.ppe_ids = this.hazardous_substance.ppe.map((ppe) => ppe.id);

        // Set the current expiry date if it was previously set.
        if ( this.hazardous_substance.sds_expires_at ) {
          this.selectedExpiryDate = moment.unix(this.hazardous_substance.sds_expires_at);
        }
      },
      (error) => {
        this.utils.showModal('Error', error.message);
      });
    }
  }

  private update() {
    const singular = this.utils.getLangTerm('hazardous-substances.singular', 'Hazardous Substance');
    this.oldApi.laravelApiRequest(
      'put',
      'hazardous_substances/' + this.hazardous_substance.id,
      this.hazardous_substance,
      {},
      () => {
        this.utils.showToast(`The ${singular} was updated.`);
        this.inProgress = false;
        this.find();
      },
      (error) => {
        this.utils.showModal('Error', error.message);
        this.inProgress = false;
      }
    );
  }

  private create() {
    const singular = this.utils.getLangTerm('hazardous-substances.singular', 'Hazardous Substance');
    this.oldApi.laravelApiRequest(
      'post',
      'hazardous_substances',
      this.hazardous_substance,
      {},
      (response) => {
        this.utils.showToast(`The ${singular} was created.`);
        this.hazardous_substance.id = response.data.id;

        this.uploadSelectedFiles(this.hazardous_substance.id);

        this.router.navigate(['/hazardous_substances']);
        this.inProgress = false;
      },
      (error) => {
        this.utils.showModal('Error', error.message);
        this.inProgress = false;
      }
    );
  }

  /**
   * Change the unit type based on the Hazardous Substance type.
   * @param hazardous_substance_type
   */
  onChangeUnitType(hazardous_substance_type: string) {
    this.hazardous_substance.hazardous_substance_unit_type = this.utils.getHazardousSubstanceUnitType(hazardous_substance_type);
  }

  /**
   * Open the PPE selector to select new PPE records to associate the hazardous substance with.
   */
  onManagePPE() {
    this.utils.showComponentDialog(PpeSelectorComponent, {
      multiple: true,
      selected: this.hazardous_substance.ppe_ids
    }, {
      width: '1200px'
    })
      .then((response) => {
        if ( typeof response != 'undefined' ) {
          this.hazardous_substance.ppe_ids = response;
        }
      })
  }
}
